/**
 * ============================================================================
 *  File        : frontend/src/api/cases.ts
 *  Module      : api
 *  Purpose     : 案件（v13 unified market_cases）型別化 API — list / detail / create
 *  Author      : EF Engineering
 *  Created     : 2026-05-31
 *  Updated     : 2026-05-31
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  後端 handler：/api/v1/cases（v13 unified market_cases model）。
 *  ⚠️ disk 上的 openapi.json 仍是舊 V12Collab shape，與正在執行的 v13 binary
 *  不一致；以「執行中 binary 的實際 response」為唯一真相（CLAUDE.md §7）。
 *  因此這裡手寫 v13 契約型別，不從 generated schema.d.ts 取（後者已 stale）。
 *  Decimal 欄位（重量 / 單價 / 數量）一律以「字串」收送，前端不得轉 float。
 */

import {
  API_BASE,
  mutationHeaders,
  requestData,
  requestEnvelope,
  type Envelope,
  type Meta,
} from './client'

/** 案件列表/總覽用的 case 主檔 DTO（v13 unified shape，對齊後端 v13_cases::CaseDto）。 */
export interface CaseDto {
  id: number
  case_no: string
  title: string
  status: string
  workflow_state: string | null
  owner_org_id: number
  member_org_id: number | null
  venue_org_id: number | null
  currency_code: string
}

/** 案件品項輸出 DTO（v13 unified shape，對齊後端 v13_cases::CaseItemDto）。 */
export interface CaseItemDto {
  id: number
  line_no: number
  item_name: string
  estimated_weight_kg: string | null
  min_unit_price: string | null
  target_unit_price: string | null
  quantity: string | null
  uom: string
}

/** 案件明細 DTO（case + items）。 */
export interface CaseDetailDto extends CaseDto {
  items: CaseItemDto[]
}

/** 建立案件品項的 request body（v13 create item DTO）。 */
export interface CreateCaseItemDto {
  item_name: string
  declared_weight_kg: string
  min_unit_price: string
  quantity: string
  uom: string
  target_unit_price?: string
  packaging?: string
  bag_unit_weight_kg?: string
}

/** 建立案件的 request body（v13 create DTO，對齊後端 v13_cases::CreateCaseDto）。 */
export interface CreateCaseDto {
  case_no?: string
  title?: string
  description?: string
  items: CreateCaseItemDto[]
  /** 出品メンバー組織 ID（EF 代理出品時 owner ≠ member）。省略時は owner org になる。 */
  member_org_id?: number
}

/** GET /api/v1/cases 的 query 參數。 */
export interface ListCasesQuery {
  page?: number
  perPage?: number
  status?: string
  caseNo?: string
}

const CASES_PATH = `${API_BASE}/cases`

/** 列出案件（分頁），回傳 { data, meta }。 */
export function listCases(query: ListCasesQuery = {}): Promise<Envelope<CaseDto[]>> {
  const params = new URLSearchParams()
  if (query.page !== undefined) params.set('page', String(query.page))
  if (query.perPage !== undefined) params.set('per_page', String(query.perPage))
  if (query.status) params.set('status', query.status)
  if (query.caseNo) params.set('case_no', query.caseNo)
  const qs = params.toString()
  return requestEnvelope<CaseDto[]>(`${CASES_PATH}${qs ? `?${qs}` : ''}`, { method: 'GET' })
}

/** 取得單一案件明細（case + items）。 */
export function getCase(id: number | string): Promise<CaseDetailDto> {
  return requestData<CaseDetailDto>(`${CASES_PATH}/${id}`, { method: 'GET' })
}

/** 建立案件（自動帶 Idempotency-Key），回傳建立後的明細。 */
export function createCase(body: CreateCaseDto): Promise<CaseDetailDto> {
  return requestData<CaseDetailDto>(CASES_PATH, {
    method: 'POST',
    body,
    headers: mutationHeaders(),
  })
}

// ---- 品項更新（update item，畫面 /cases/:id/items）-------------------------

/**
 * 更新品項的 request body（對齊後端 v13_listing::UpdateItemDto）。
 * 全欄位 optional：給了即設成該值，不給則不動。
 * ⚠️ Decimal 欄位（min_price / target_price / estimated_weight_kg）一律字串送出，
 * 前端不得轉 float（CLAUDE.md §2）。後端以 rust_decimal::Decimal 反序列化字串。
 */
export interface UpdateCaseItemDto {
  description?: string
  min_price?: string
  target_price?: string
  estimated_weight_kg?: string
  package_form?: string
  reason?: string
}

/** 後端 update_item 回傳的 listing item view（對齊 v13_listing::ListingItemDto）。 */
export interface ListingItemDto {
  item_id: number
  line_no: number
  description: string
  min_price: string | null
  target_price: string | null
  package_form: string | null
  estimated_weight_kg: string | null
  complete: boolean
}

/** update_item 回傳包裝（對齊 v13_listing::ListingItemUpdatedDto）。 */
interface ListingItemUpdatedDto {
  item: ListingItemDto
}

/**
 * 更新單一品項（PUT /api/v1/cases/:case_id/items/:item_id，自動帶 Idempotency-Key）。
 * RBAC：marketplace.cases.update（後端 gate）。回傳更新後的 listing item view。
 */
export async function updateCaseItem(
  caseId: number | string,
  itemId: number | string,
  body: UpdateCaseItemDto,
): Promise<ListingItemDto> {
  const result = await requestData<ListingItemUpdatedDto>(
    `${CASES_PATH}/${caseId}/items/${itemId}`,
    { method: 'PUT', body, headers: mutationHeaders() },
  )
  return result.item
}

// ---- 設定会場（venue）------------------------------------------------------

/**
 * 会場 org 選項（= 後端 /organizations/directory 中 org_type=venue 的組織）。
 * ⚠️ set_venue 的 venue_org_id 是「組織 id」（非 master.venues 的場地 id）；
 * 故會場下拉以 org directory 的 venue 類型組織為來源（執行中 binary 已驗 2026-06-01：
 * org id 3 = 朝日 venue，正是 set_venue 接受的值）。
 */
export interface VenueOrgOption {
  id: number
  name: string
  orgType: string
}

/** set_venue 結果（對齊後端 CaseVenueSetDto）。 */
export interface CaseVenueSetDto {
  case_id: number
  member_org_id: number | null
  venue_org_id: number | null
  workflow_state: string | null
}

/** org directory 單列原始 shape（只取需要的欄位，其餘忽略）。 */
interface OrgDirectoryRow {
  id: number
  name: string
  org_type?: string | null
  kind?: string | null
  type?: string | null
}

/**
 * 列出可作為「会場」的組織（org_type=venue）。
 * 後端：GET /api/v1/organizations/directory（RBAC：iam.organizations.list）。
 * 容忍 org_type / kind / type 任一欄位命名。
 */
export async function listVenueOrgs(): Promise<VenueOrgOption[]> {
  const rows = await requestData<OrgDirectoryRow[]>(`${API_BASE}/organizations/directory`, {
    method: 'GET',
  })
  return rows
    .map((r) => ({ id: r.id, name: r.name, orgType: r.org_type ?? r.kind ?? r.type ?? '' }))
    .filter((r) => r.orgType.toLowerCase() === 'venue')
}

/**
 * 列出可作為「出品メンバー」的組織（org_type=member）。
 * EF 代理出品時用來選擇 member org（owner=EF, member=億成 等）。
 * 後端：GET /api/v1/organizations/directory（RBAC：iam.organizations.list）。
 */
export async function listMemberOrgs(): Promise<VenueOrgOption[]> {
  const rows = await requestData<OrgDirectoryRow[]>(`${API_BASE}/organizations/directory`, {
    method: 'GET',
  })
  return rows
    .map((r) => ({ id: r.id, name: r.name, orgType: r.org_type ?? r.kind ?? r.type ?? '' }))
    .filter((r) => r.orgType.toLowerCase() === 'member')
}

/**
 * 設定会場（PUT /api/v1/cases/:id/venue，自動帶 Idempotency-Key）。
 * 兩方 org 齊備後後端會把 workflow_state 推進到 DetailAccepted（執行中 binary 已驗）。
 * RBAC：marketplace.cases.update。
 */
export function setVenue(
  caseId: number | string,
  venueOrgId: number,
): Promise<CaseVenueSetDto> {
  return requestData<CaseVenueSetDto>(`${CASES_PATH}/${caseId}/venue`, {
    method: 'PUT',
    body: { venue_org_id: venueOrgId },
    headers: mutationHeaders(),
  })
}

export type { Meta }
